import { LanguageBadge } from '@components/common/LanguageBadge';
import { useLocalizedFormLanguage } from '@components/common/LocalizedFormLanguageProvider';
import InputWithLabelT from '@components/tailwind-components/Input/InputWithLabelT';
import type React from 'react';
import { useEffect } from 'react';
import { type Control, useWatch } from 'react-hook-form';

interface IProps {
  // Path of the language map itself, e.g. 'options.subtitle'. Each language
  // gets its own field under it.
  field: string;
  label: string;
  placeholder: string;
  control: Control<any>;
  id: string;
  // Validation message. What it guards is the default language's value, since
  // that is what every empty translation falls back to.
  required?: string;
  inputClassName?: string;
  labelClassName?: string;
}

export const LocalizedInputWithLabel: React.FC<IProps> = ({
  field,
  label,
  placeholder,
  control,
  id,
  required,
  inputClassName = '',
  labelClassName = '',
}) => {
  const formLanguage = useLocalizedFormLanguage();
  const defaultLanguage = formLanguage?.defaultLanguage ?? '';
  const defaultLanguageText = useWatch({
    control,
    name: `${field}.${defaultLanguage}`,
  });

  const languages = formLanguage?.languages ?? [];
  const currentLanguage = formLanguage?.currentLanguage ?? '';

  // A failed submit leaves a native validity message on the input, and the
  // switcher can then hide that input. The browser refuses to submit a form
  // holding an invalid control it cannot focus, and it refuses before
  // react-hook-form runs - so the message that would clear it never gets the
  // chance to. Drop it from every language that is off screen.
  useEffect(() => {
    for (const language of languages) {
      if (language === currentLanguage) {
        continue;
      }

      const input = document.getElementById(`${id}-${language}`);

      if (input instanceof HTMLInputElement) {
        input.setCustomValidity('');
      }
    }
  }, [languages, currentLanguage, id]);

  if (!formLanguage) {
    return null;
  }

  const { isMultiLanguage } = formLanguage;

  // While another language is on screen the message would otherwise point at
  // the field in front of the user, which is not the empty one.
  const requiredMessage =
    required && currentLanguage !== defaultLanguage
      ? `${required} (${defaultLanguage.toUpperCase()})`
      : required;

  return (
    <>
      {languages.map((language) => {
        const isDefaultLanguage = language === defaultLanguage;
        const isActiveLanguage = language === currentLanguage;

        return (
          <div key={language} className={isActiveLanguage ? '' : '!hidden'}>
            <InputWithLabelT
              type='text'
              field={`${field}.${language}`}
              id={`${id}-${language}`}
              label={label}
              // Says which language this field holds. Only worth showing when
              // there is a switcher to act on - with a single language every
              // field is trivially that language.
              labelSubText={
                isMultiLanguage ? (
                  // BadgeT is itself `flex`, so unwrapped it would break onto
                  // its own row instead of sitting on the label's line.
                  <span className='inline-block align-middle ml-1'>
                    <LanguageBadge language={language} />
                  </span>
                ) : undefined
              }
              // An empty translation falls back to the default language at
              // runtime, so show that copy instead of the generic hint.
              placeholder={
                (!isDefaultLanguage && defaultLanguageText) || placeholder
              }
              control={control}
              // The rule has to sit on whichever input is on screen. Native
              // validation focuses the failing control, and a hidden one
              // cannot be focused - the browser then blocks the submit with
              // nothing to show for it.
              options={
                required
                  ? {
                      validate: () =>
                        !isActiveLanguage ||
                        !!defaultLanguageText ||
                        requiredMessage,
                    }
                  : undefined
              }
              inputClassName={inputClassName}
              labelClassName={labelClassName}
            />
          </div>
        );
      })}
    </>
  );
};

export default LocalizedInputWithLabel;
